import { Paragraph, TextRun, ExternalHyperlink } from 'docx';
import { NORMALIZED_TITLE_KEYWORDS } from '../constants/resumeKeywords';
import { isLinkedInLine, extractLinkedInUrl, parseTextWithBold } from './downloadUtils';

// Month names and 4-digit years — used to detect date-bearing lines
const DATE_PAT = /(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|Present|\d{4})/i;
// En-dash / hyphen with surrounding spaces — standard separator in resume lines
const DASH_PAT = /\s[-–]\s/;
// "Word, XX" — City, State abbreviation (two uppercase letters)
const CITY_STATE_PAT = /[A-Z][a-z]+,\s*[A-Z]{2}/;

function sectionKey(text) {
  return text.trim().toLowerCase();
}

// Returns true if any non-heading body content exists between startIdx and the
// next section heading (or end of array). Used to skip empty CV section headers.
function hasBodyContent(lines, startIdx) {
  for (let j = startIdx; j < lines.length; j++) {
    const t2 = lines[j].trim();
    if (!t2) continue;
    // Another section heading — no body content found before it
    if (
      (t2 === t2.toUpperCase() && /[A-Z]/.test(t2) && t2.length <= 60 &&
        !DASH_PAT.test(t2) && !/^[•\-–—]/.test(t2) && !/[.!?]$/.test(t2)) ||
      (NORMALIZED_TITLE_KEYWORDS.has(t2.toLowerCase()) && !DASH_PAT.test(t2))
    ) {
      return false;
    }
    return true;
  }
  return false;
}

/**
 * Build the body paragraphs for a resume/CV DOCX.
 *
 * Rules are evaluated in strict priority order (0–9).
 * currentSection is the lowercased text of the most recently seen section
 * heading and drives context-sensitive routing in Rules 4–8.
 *
 * @param {string[]} lines   — array of text lines starting after the header block
 * @param {object}   item    — result item (docType etc. available for future use)
 * @returns {Paragraph[]}
 */
export function buildResumeParagraphs(lines, item) {
  const paragraphs = [];
  let currentSection = '';
  let i = 0;

  while (i < lines.length) {
    const raw = lines[i];
    const t = raw.trim();

    // ── RULE 0 — Empty line ───────────────────────────────────────────────
    if (!t) {
      paragraphs.push(
        new Paragraph({ style: 'ResumeBody', children: [new TextRun({ text: '' })] })
      );
      i++;
      continue;
    }

    // ── RULE 1 — LinkedIn hyperlink line ──────────────────────────────────
    if (isLinkedInLine(t)) {
      const url = extractLinkedInUrl(t);
      if (url) {
        paragraphs.push(
          new Paragraph({
            style: 'ResumeBody',
            children: [
              new ExternalHyperlink({
                link: url,
                children: [
                  new TextRun({ text: url, font: 'Calibri', size: 22, color: '0563C1', underline: {} })
                ]
              })
            ]
          })
        );
        i++;
        continue;
      }
    }

    // ── RULE 2 — ALL-CAPS section header ──────────────────────────────────
    // Must be: all-uppercase, contain at least one letter, ≤ 60 chars,
    // no dash separator, not a bullet, not ending in sentence punctuation.
    // Skip entirely if no body content follows (empty CV sections).
    if (
      t === t.toUpperCase() &&
      /[A-Z]/.test(t) &&
      t.length <= 60 &&
      !DASH_PAT.test(t) &&
      !/^[•\-–—]/.test(t) &&
      !/[.!?]$/.test(t)
    ) {
      if (hasBodyContent(lines, i + 1)) {
        currentSection = sectionKey(t);
        paragraphs.push(
          new Paragraph({ style: 'ResumeHeading', children: [new TextRun({ text: t })] })
        );
      }
      i++;
      continue;
    }

    // ── RULE 3 — Known Title-Case section keyword, no dash ────────────────
    // Skip entirely if no body content follows (empty CV sections).
    if (NORMALIZED_TITLE_KEYWORDS.has(t.toLowerCase()) && !DASH_PAT.test(t)) {
      if (hasBodyContent(lines, i + 1)) {
        currentSection = sectionKey(t);
        paragraphs.push(
          new Paragraph({ style: 'ResumeHeading', children: [new TextRun({ text: t })] })
        );
      }
      i++;
      continue;
    }

    // ── RULE 4 — Non-experience sections: always plain ────────────────────
    // Education, Technical Skills, and Additional Information lines must never
    // receive bold or italic treatment — institution/degree lines would otherwise
    // be misclassified as company lines.
    if (/education|technical|additional/i.test(currentSection)) {
      paragraphs.push(
        new Paragraph({ style: 'ResumeBody', children: parseTextWithBold(t) })
      );
      i++;
      continue;
    }

    // ── RULE 5 — Job title line (in experience section) ───────────────────
    // Has a dash separator AND a month/year AND we are inside an experience section.
    if (DASH_PAT.test(t) && DATE_PAT.test(t) && /experience/i.test(currentSection)) {
      const displayLine = t.replace(/\s*\|\s*/g, ' – ');
      paragraphs.push(
        new Paragraph({ style: 'ResumeJobTitle', children: [new TextRun({ text: displayLine })] })
      );
      i++;
      continue;
    }

    // ── RULE 6 — Company name line (in experience section) ────────────────
    // Has a dash separator AND a City, State pattern AND no month/year.
    if (DASH_PAT.test(t) && CITY_STATE_PAT.test(t) && !DATE_PAT.test(t) && /experience/i.test(currentSection)) {
      paragraphs.push(
        new Paragraph({
          style: 'ResumeCompany',
          children: [new TextRun({ text: t, bold: true })]
        })
      );
      i++;
      continue;
    }

    // ── RULE 7 — Scope / context sentence (resume / cv only) ─────────────
    // Lines starting with a role-scope verb that contain no date.
    // Guarded to resume/cv: bio/linkedin/portfolio body sentences frequently
    // open with these verbs and must not receive italic scope treatment.
    if (
      ['resume', 'cv'].includes(item?.docType) &&
      /^(?:Oversaw|Led|Directed|Managed|Owned)\b/i.test(t) &&
      !DATE_PAT.test(t)
    ) {
      paragraphs.push(
        new Paragraph({ style: 'ResumeScope', children: parseTextWithBold(t) })
      );
      i++;
      continue;
    }

    // ── RULE 8 — Bullet line ─────────────────────────────────────────────
    if (/^[•]/.test(t)) {
      if (/competencies|skills/i.test(currentSection)) {
        // Competency / skills sections: collect all consecutive bullets and
        // join them inline as "kw • kw • kw" on a single ResumeBody paragraph.
        const keywords = [];
        while (i < lines.length && /^[•]/.test(lines[i].trim())) {
          keywords.push(lines[i].replace(/^\s*•\s*/, '').trim());
          i++;
        }
        paragraphs.push(
          new Paragraph({
            style: 'ResumeBody',
            children: parseTextWithBold(keywords.join(' • '))
          })
        );
        continue; // i already advanced past the collected bullets
      }

      // All other sections: one bullet per paragraph with hanging indent.
      const bulletContent = t.replace(/^•\s*/, '');
      paragraphs.push(
        new Paragraph({
          style: 'ResumeBullet',
          children: [
            new TextRun({ text: '• ', font: 'Calibri', size: 22 }),
            ...parseTextWithBold(bulletContent)
          ]
        })
      );
      i++;
      continue;
    }

    // ── RULE 9 — Fallback: plain body ─────────────────────────────────────
    paragraphs.push(
      new Paragraph({ style: 'ResumeBody', children: parseTextWithBold(t) })
    );
    i++;
  }

  return paragraphs;
}
